import { HttpTransportType, HubConnectionBuilder, HubConnectionState } from "@microsoft/signalr";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Frame, FrameHeader, getApiUrl, OmniUser, RecordType, sessionValue } from "../../..";
import { RadarCameraExtrinsicFitter } from "../../../models/visualizer/FittingTool";
import { NumericInput } from "../../NumericInput";
import { DataChart } from "./DataChart";
import {
    buildCalibrationCameraDatasets,
    buildCalibrationRadarDatasets,
    buildCameraDatasets,
    buildFusionTargetDatasets,
    buildInferredObjectBounds,
    buildRadarPointDatasets,
    buildRadarTargetDatasets,
    computeFrameFusionObservations,
    findHoveredInferredObject,
    HoveredInferredObject,
} from "./calibrationDatasets";
import { appendAndParse } from "./calibrationFrameParser";

type CalibrationProps = {
    deviceId: string;
    backgroundImageUrl?: string;
    /** Device mount height (Device.height). */
    deviceHeight?: number;
    /** Calibrated pitch offset in radians (Device.fusionOffsetPitch). */
    fusionOffsetPitchRadians?: number;
    /** Calibrated roll offset in radians (Device.fusionOffsetRoll). */
    fusionOffsetRollRadians?: number;
    /** Calibrated yaw offset in radians (Device.fusionOffsetYaw). */
    fusionOffsetYawRadians?: number;
    /** Averaged accelerometer pitch in radians, derived from recent hardware status reports. */
    accelerometerPitchRadians?: number;
    /** Averaged accelerometer roll in radians, derived from recent hardware status reports. */
    accelerometerRollRadians?: number;
    /** When true, the "Show debugging tools" toggle is rendered, exposing the
     * raw radar/camera views and the data-source / console panels. */
    isSuperAdmin?: boolean;
    /** Persist the current calibration result to the device. The parent is
     * responsible for the API call, toast confirmation, and refreshing device
     * data. All angle values are in radians. */
    onApplyToDevice?: (params: { pitchRad: number; rollRad: number; yawRad: number; heightM: number }) => Promise<void>;
};

type StreamPayload = string | number[] | Uint8Array;
type StreamSubscription = { dispose: () => void };

const STREAM_WAIT_WARN_SECONDS = 20;
const STREAM_WAIT_TIMEOUT_SECONDS = 60;
const STREAM_WAIT_FAILED_MESSAGE = "The device isn't streaming — it may be offline.";
const AnimatedEllipsis: React.FC = () => (
    <>
        <style>{`
            @keyframes omni-ellipsis { 0%, 20% { opacity: 0.15 } 50% { opacity: 1 } 100% { opacity: 0.15 } }
            .omni-ellipsis-dot { animation: omni-ellipsis 1.4s infinite both; }
            .omni-ellipsis-dot:nth-child(2) { animation-delay: 0.2s; }
            .omni-ellipsis-dot:nth-child(3) { animation-delay: 0.4s; }
        `}</style>
        <span aria-hidden="true">
            <span className="omni-ellipsis-dot">.</span>
            <span className="omni-ellipsis-dot">.</span>
            <span className="omni-ellipsis-dot">.</span>
        </span>
    </>
);

const IMAGE_WIDTH = 640;
const IMAGE_HEIGHT = 480;

// Intrinsics for the 1920x1080 native camera, scaled to the 640x480 display.
const INTR_SCALED = {
    fx: 1091.48385 * (640 / 1920),
    fy: 1094.62827 * (480 / 1080),
    cx: 971.484941 * (640 / 1920),
    cy: 492.295 * (480 / 1080),
};

const DIST = {
    k1: -0.39083237,
    k2: 0.17261686,
    p1: 0.00044551,
    p2: 0.00221091,
    k3: -0.03969883,
};

export const Calibration: React.FC<CalibrationProps> = (props) => {
    const { deviceId, backgroundImageUrl } = props;
    const [connectionState, setConnectionState] = useState<HubConnectionState>(HubConnectionState.Disconnected);
    const [messages, setMessages] = useState<string[]>([]);
    const [waitElapsed, setWaitElapsed] = useState<number>(0);
    const [streamError, setStreamError] = useState<string | null>(null);
    const streamRef = useRef<StreamSubscription | null>(null);
    const simulationTimerRef = useRef<number | null>(null);
    const playbackTimerRef = useRef<number | null>(null);
    const bufferRef = useRef<Uint8Array>(new Uint8Array(0));
    const [simFileName, setSimFileName] = useState<string>("");
    const [simFileSize, setSimFileSize] = useState<number>(0);
    const [simData, setSimData] = useState<Uint8Array | null>(null);
    const [simChunkIndex, setSimChunkIndex] = useState<number>(0);
    const [isSimulating, setIsSimulating] = useState<boolean>(false);
    const [frames, setFrames] = useState<Frame[]>([]);
    const [currentFrameIndex, setCurrentFrameIndex] = useState<number>(0);
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [playbackFps, setPlaybackFps] = useState<number>(10);
    const [scrubMaxFrameIndex, setScrubMaxFrameIndex] = useState<number | null>(null);
    const [activeBottomPanel, setActiveBottomPanel] = useState<"datasource" | "console" | null>(null);
    const [activeChartTab, setActiveChartTab] = useState<"radar" | "camera" | "calibration">("calibration");
    const [showDebugTools, setShowDebugTools] = useState<boolean>(false);
    const [isApplyingToDevice, setIsApplyingToDevice] = useState<boolean>(false);
    const [dataSource, setDataSource] = useState<"sim" | "device">("device");
    const [accPitch, setAccPitch] = useState<number>(props.accelerometerPitchRadians ?? 0);
    const [accRoll, setAccRoll] = useState<number>(props.accelerometerRollRadians ?? 0);
    const [calPitch, setCalPitch] = useState<number>(props.fusionOffsetPitchRadians ?? 0);
    const [calRoll, setCalRoll] = useState<number>(props.fusionOffsetRollRadians ?? 0);
    const [calYaw, setCalYaw] = useState<number>(props.fusionOffsetYawRadians ?? 0);
    const [fitter, setFitter] = useState<RadarCameraExtrinsicFitter | null>(null);
    const [deviceHeight, setDeviceHeight] = useState<number>(props.deviceHeight ?? 9.5);
    const [hasRunCalibration, setHasRunCalibration] = useState<boolean>(false);
    const [calibrationStatus, setCalibrationStatus] = useState<string | null>(null);

    // Accelerometer values are read-only — keep them in sync with the latest
    // averaged reading provided by the parent.
    useEffect(() => {
        if (props.accelerometerPitchRadians !== undefined) setAccPitch(props.accelerometerPitchRadians);
    }, [props.accelerometerPitchRadians]);
    useEffect(() => {
        if (props.accelerometerRollRadians !== undefined) setAccRoll(props.accelerometerRollRadians);
    }, [props.accelerometerRollRadians]);

    // Device settings (height, fusion offsets) seed local state once when they
    // first arrive from the parent's async fetch. After that the user owns
    // them via the inputs / Do Calibration result.
    const calPitchSeededRef = useRef(props.fusionOffsetPitchRadians !== undefined);
    useEffect(() => {
        if (!calPitchSeededRef.current && props.fusionOffsetPitchRadians !== undefined) {
            setCalPitch(props.fusionOffsetPitchRadians);
            calPitchSeededRef.current = true;
        }
    }, [props.fusionOffsetPitchRadians]);
    const calRollSeededRef = useRef(props.fusionOffsetRollRadians !== undefined);
    useEffect(() => {
        if (!calRollSeededRef.current && props.fusionOffsetRollRadians !== undefined) {
            setCalRoll(props.fusionOffsetRollRadians);
            calRollSeededRef.current = true;
        }
    }, [props.fusionOffsetRollRadians]);
    const calYawSeededRef = useRef(props.fusionOffsetYawRadians !== undefined);
    useEffect(() => {
        if (!calYawSeededRef.current && props.fusionOffsetYawRadians !== undefined) {
            setCalYaw(props.fusionOffsetYawRadians);
            calYawSeededRef.current = true;
        }
    }, [props.fusionOffsetYawRadians]);

    const deviceHeightSeededRef = useRef(props.deviceHeight !== undefined);
    useEffect(() => {
        if (!deviceHeightSeededRef.current && props.deviceHeight !== undefined) {
            setDeviceHeight(props.deviceHeight);
            deviceHeightSeededRef.current = true;
        }
    }, [props.deviceHeight]);

    const [hoveredInferredObject, setHoveredInferredObject] = useState<HoveredInferredObject | null>(null);
    const simTotalChunks = 1;
    const useSignalR = dataSource === "device";

    const hubUrl = getApiUrl(`/hubs/sensor`);
    const [user] = sessionValue<OmniUser>("currentUser");
    const authToken = user?.authToken ?? "";
    const frameHeaderLength = useMemo(() => new FrameHeader().getLength(), []);

    const pushMessage = (line: string) => {
        setMessages((prev) => [...prev.slice(-199), line]);
    };

    const pushMessages = (lines: string[]) => {
        if (!lines.length) return;
        setMessages((prev) => [...prev, ...lines].slice(-200));
    };

    const stopSimulation = () => {
        if (simulationTimerRef.current !== null) {
            window.clearInterval(simulationTimerRef.current);
            simulationTimerRef.current = null;
        }
        setIsSimulating(false);
    };

    const stopPlayback = () => {
        if (playbackTimerRef.current !== null) {
            window.clearInterval(playbackTimerRef.current);
            playbackTimerRef.current = null;
        }
        setIsPlaying(false);
    };

    const resetStreamState = () => {
        bufferRef.current = new Uint8Array(0);
        setFrames([]);
        setCurrentFrameIndex(0);
        setIsPlaying(false);
        setCalibrationStatus(null);
    };

    const appendBytesAndParse = (bytes: Uint8Array) => {
        if (!bytes.length) return;
        const { frames: parsedFrames, remainder, logs } = appendAndParse(bufferRef.current, bytes, frameHeaderLength);
        bufferRef.current = remainder;
        pushMessages(logs);
        if (parsedFrames.length > 0) {
            setFrames((prev) => [...prev, ...parsedFrames]);
        }
    };

    // Live status shown in the stream panel while a device stream has yet to
    // deliver a frame, so landing on the page gives feedback rather than a
    // static "waiting" line. Only used for the device data source.
    const renderStreamWaitStatus = () => {
        let content: React.ReactNode;
        if (streamError) {
            content = streamError;
        } else if (connectionState !== HubConnectionState.Connected) {
            content = "Connecting…";
        } else if (waitElapsed < STREAM_WAIT_WARN_SECONDS) {
            content = <>Waiting for the device to start streaming<AnimatedEllipsis /></>;
        } else {
            const pct = Math.min(100, Math.round((waitElapsed / STREAM_WAIT_TIMEOUT_SECONDS) * 100));
            content = (
                <div className="space-y-1">
                    <div>Device is slow to respond… {waitElapsed}s / {STREAM_WAIT_TIMEOUT_SECONDS}s</div>
                    <div className="h-1 w-40 rounded" style={{ backgroundColor: "rgba(148,163,184,0.35)" }}>
                        <div className="h-1 rounded" style={{ width: `${pct}%`, backgroundColor: "#FACC15" }} />
                    </div>
                </div>
            );
        }
        return <div className="text-sm">{content}</div>;
    };

    /**
     * Apply the current calibration result to the device via the parent's
     * onApplyToDevice callback. On success, reset the streaming buffer and
     * the fit state so freshly-streamed data can be used to verify the new
     * settings. Toast confirmation and device-data refresh are owned by the
     * parent.
     */
    const applyToDevice = async () => {
        if (!props.onApplyToDevice || !hasRunCalibration || isApplyingToDevice) return;
        setIsApplyingToDevice(true);
        try {
            await props.onApplyToDevice({
                pitchRad: calPitch,
                rollRad: calRoll,
                yawRad: calYaw,
                heightM: deviceHeight,
            });
            // Reset so the user sees fresh data streaming in under the new
            // device settings and can re-calibrate to verify.
            stopPlayback();
            resetStreamState();
            setHasRunCalibration(false);
            fitter?.clear();
            pushMessage("Settings applied. Streaming new data...");
        } catch (err) {
            pushMessage(`Apply failed: ${String(err)}`);
        } finally {
            setIsApplyingToDevice(false);
        }
    };

    const doCalibration = () => {
        if (!fitter || !frames) return;

        pushMessage("Starting calibration...");
        setCalibrationStatus(null);

        // Reset to accelerometer-based seed so each calibration click starts
        // fresh — not carrying over a stale pose from a previous click.
        fitter.setInitialEstimate(0, accPitch, accRoll, deviceHeight);

        const accumulateAllFrames = () => {
            frames.forEach((frame, frameIndex) => {
                const inferredObjects = frame.tlvs.find(tlv => tlv.getDataType() === RecordType.InferredObject);
                if (!inferredObjects) return;
                const tracks = frame.tlvs.find(tlv => tlv.getDataType() === RecordType.RadarTarget);
                if (!tracks) return;
                const cloud = frame.tlvs.find(tlv => tlv.getDataType() === RecordType.PointCloud);
                const assoc = frame.tlvs.find(tlv => tlv.getDataType() === RecordType.RadarTargetIndex);

                const filteredInferredObjects = inferredObjects.inferredObjects.filter((obj) => obj.classId !== 8);
                fitter.addFrame(frameIndex, cloud?.pointClouds ?? [], tracks.radarTargets, assoc?.targetIndices ?? [], filteredInferredObjects);
            });
        };

        // EM-style outer loop: re-associate at the new pose, re-fit, repeat.
        // The first iteration's associations are biased by accelerometer pose
        // error; subsequent iterations use the fitter's improved pose so the
        // associations clean up and the optimizer converges to the truth.
        // We track the best-RMSE iteration and stop on the first non-improvement
        // — once near the optimum, association noise can make later iterations
        // bounce around (and even overshoot), so the last iter is not always
        // the best one.
        const maxIter = 5;
        const rmseImprovementTolPx = 0.5;
        let report: ReturnType<typeof fitter.fitBatch> = null;
        let prevRmse = Number.POSITIVE_INFINITY;

        for (let iter = 0; iter < maxIter; iter++) {
            fitter.clear();
            accumulateAllFrames();

            pushMessage(`Iter ${iter + 1}: fitting batch...`);
            const iterReport = fitter.fitBatch();
            if (!iterReport) {
                pushMessage(`Iter ${iter + 1}: fit failed (insufficient samples?)`);
                break;
            }

            pushMessage(
                `Iter ${iter + 1}: RMSE=${iterReport.rmsePx.toFixed(2)}px, ` +
                `pitch=${(iterReport.pitchRad * 180 / Math.PI).toFixed(2)}°, ` +
                `yaw=${(iterReport.yawRad * 180 / Math.PI).toFixed(2)}°, ` +
                `roll=${(iterReport.rollRad * 180 / Math.PI).toFixed(2)}°, ` +
                `height=${iterReport.heightM.toFixed(2)}m`
            );

            // Track the best iteration — later iterations can overshoot and
            // we don't want to throw away a good fit by chasing one.
            if (!report || iterReport.rmsePx < report.rmsePx) {
                report = iterReport;
            }

            // Stop on the first iteration that fails to improve by at least
            // the tolerance. Non-improvement means associations have stabilized
            // and we're now bouncing around the minimum.
            const improvementPx = prevRmse - iterReport.rmsePx;
            if (improvementPx < rmseImprovementTolPx) {
                pushMessage(`Iter ${iter + 1}: stopping (Δ=${improvementPx.toFixed(2)}px). Best RMSE=${report.rmsePx.toFixed(2)}px.`);
                break;
            }
            prevRmse = iterReport.rmsePx;
        }

        // Re-accumulate samples at the best pose so the live RMSE display
        // (which evaluates against fitter.samples) matches the value we
        // report. The iteration loop's RMSE was computed against samples
        // accumulated at the *previous* iter's pose, so it doesn't agree
        // with evaluating that same pose against samples that would have
        // been associated using the pose itself. After this re-accumulation,
        // evaluateAt at the report pose returns the same RMSE we report,
        // and manual tweaks won't appear to "find a better answer".
        if (report) {
            fitter.setInitialEstimate(report.yawRad, report.pitchRad, report.rollRad, report.heightM);
            fitter.clear();
            accumulateAllFrames();
            const reEval = fitter.evaluateAt(report.yawRad, report.pitchRad, report.rollRad, report.heightM);
            if (reEval) {
                pushMessage(
                    `Final RMSE at best pose: ${reEval.rmsePx.toFixed(2)}px ` +
                    `(loop reported ${report.rmsePx.toFixed(2)}px before sample realignment)`
                );
                report = {
                    ...report,
                    rmsePx: reEval.rmsePx,
                    meanPx: reEval.meanPx,
                    medianPx: reEval.medianPx,
                    maxPx: reEval.maxPx,
                    usedSamples: reEval.usedSamples,
                    droppedAsOutliers: reEval.droppedAsOutliers,
                };
            }
        }

        // Post-search over height: the EM loop kept height fixed at the
        // user's entered value (so the iterations stay deterministic), but
        // a slightly different height may further reduce RMSE. Sweep 20
        // values across ±10% of the entered height against the already-
        // accumulated samples and pick the lowest-RMSE one.
        if (report) {
            const numHeightSamples = 20;
            const heightLo = deviceHeight * 0.9;
            const heightHi = deviceHeight * 1.1;
            let bestHeightM = report.heightM;
            let bestRmsePx = report.rmsePx;
            let bestEval = null as ReturnType<typeof fitter.evaluateAt>;

            for (let i = 0; i < numHeightSamples; i++) {
                const h = heightLo + (i / (numHeightSamples - 1)) * (heightHi - heightLo);
                const ev = fitter.evaluateAt(report.yawRad, report.pitchRad, report.rollRad, h);
                if (ev && ev.rmsePx < bestRmsePx) {
                    bestRmsePx = ev.rmsePx;
                    bestHeightM = h;
                    bestEval = ev;
                }
            }

            if (bestEval && bestHeightM !== report.heightM) {
                pushMessage(
                    `Height search: ${bestHeightM.toFixed(3)}m → RMSE ${bestEval.rmsePx.toFixed(2)}px ` +
                    `(was ${report.rmsePx.toFixed(2)}px at ${report.heightM.toFixed(3)}m)`
                );
                report = {
                    ...report,
                    heightM: bestHeightM,
                    rmsePx: bestEval.rmsePx,
                    meanPx: bestEval.meanPx,
                    medianPx: bestEval.medianPx,
                    maxPx: bestEval.maxPx,
                    usedSamples: bestEval.usedSamples,
                    droppedAsOutliers: bestEval.droppedAsOutliers,
                };
            } else {
                pushMessage(`Height search: entered value ${report.heightM.toFixed(3)}m was already best`);
            }
        }

        pushMessage("Done calibration...");
        console.log("Fit:", report);

        if (report) {
            pushMessage(`Fit yawDeg: ${(report.yawRad * 180 / Math.PI).toString()},
                pitchDeg: ${(report.pitchRad * 180 / Math.PI).toString()},
                rollDeg: ${(report.rollRad * 180 / Math.PI).toString()},
                used: ${report.usedSamples.toString()},
                dropped: ${report.droppedAsOutliers.toString()}`
            );

            // Store absolute value, we will rename the fields and remove "offset" in the future.
            setCalPitch(report.pitchRad);
            setCalRoll(report.rollRad);
            setCalYaw(report.yawRad);
            // Push the height-search result into the field so the live RMSE
            // display (which evaluates at deviceHeight) matches the reported
            // RMSE. The next Calibrate click will use this as the new center
            // for the height sweep, but the EM loop itself stays stable
            // because it only uses height to gate radar associations.
            setDeviceHeight(report.heightM);
            setHasRunCalibration(true);
        } else {
            // The fit bailed. Report why in the UI — the fitter only ever
            // needed more matched pairs, so tell the user how far off they are
            // and which of the two upstream causes to check.
            const matchedPairs = fitter.getSampleCount();
            const requiredPairs = fitter.getMinSamplesToFit();
            const usableFrames = frames.filter((frame) =>
                frame.tlvs.some((tlv) => tlv.getDataType() === RecordType.InferredObject) &&
                frame.tlvs.some((tlv) => tlv.getDataType() === RecordType.RadarTarget)
            ).length;

            const reason = usableFrames === 0
                ? `none of the ${frames.length} collected frames contain both radar targets and camera detections`
                : `only ${matchedPairs} of the ${requiredPairs} required radar/camera pairs matched across ${usableFrames} usable frame${usableFrames === 1 ? "" : "s"}`;

            setCalibrationStatus(
                `Not enough matched targets to calibrate — ${reason}. ` +
                `Let more traffic pass through the scene and try again. If it keeps failing, check that Device Height is set correctly — a wrong height stops targets from matching.`
            );
        }
        pushMessage(`Fit status: ${fitter.isFit}`);
    };

    // Live reprojection error at the user's currently-displayed angles. Updates
    // whenever they tweak any of the four cal* / height fields so they can see
    // whether their manual adjustment helps or hurts the fit.
    const liveError = useMemo(() => {
        if (!fitter || !hasRunCalibration) return null;
        return fitter.evaluateAt(calYaw, calPitch, calRoll, deviceHeight);
    }, [fitter, hasRunCalibration, calYaw, calPitch, calRoll, deviceHeight]);

    const startSimulation = () => {
        if (!simData) return;
        stopSimulation();
        setIsSimulating(true);
        const chunkSize = Math.ceil(simData.length / simTotalChunks);
        simulationTimerRef.current = window.setInterval(() => {
            setSimChunkIndex((prev) => {
                if (!simData) return prev;
                const start = prev * chunkSize;
                const end = Math.min(start + chunkSize, simData.length);
                const chunk = simData.slice(start, end);
                if (chunk.length > 0) {
                    appendBytesAndParse(chunk);
                }
                const nextIndex = prev + 1;
                if (nextIndex >= simTotalChunks || end >= simData.length) {
                    stopSimulation();
                }
                return nextIndex;
            });
        }, 1000);
    };

    // Recreate the fitter whenever the accelerometer seed angles change
    // Height is updated in-place via setDeviceHeight() to preserve fit state.
    useEffect(() => {
        setFitter(new RadarCameraExtrinsicFitter(
            INTR_SCALED, DIST,
            {
                pitch0Rad: accPitch,
                roll0Rad: accRoll,
                heightM: deviceHeight,
                minDetectionConfidence: 0.25,
                maxSamples: 4000,
                optimizePitch: true,
                optimizeRoll: true,
                // Height stays at the user's typed value. With it free, the
                // optimizer trades pitch and height against each other (they
                // are coupled via ground-plane projection), and the bracket
                // re-anchors at the fitted value across re-clicks — leading
                // to non-deterministic results between Calibrate clicks.
                optimizeHeight: false,
            }
        ));
        // eslint-disable-next-line react-hooks/exhaustive-deps -- deviceHeight intentionally omitted; pushed via setter below.
    }, [accPitch, accRoll]);

    useEffect(() => {
        fitter?.setDeviceHeight(deviceHeight);
    }, [fitter, deviceHeight]);

    useEffect(() => {
        if (!deviceId || !useSignalR) return;

        const connection = new HubConnectionBuilder()
            .withUrl(hubUrl, {
                transport: HttpTransportType.WebSockets,
                withCredentials: true,
                headers: {
                    "X-AUTH-TOKEN": authToken,
                },
            })
            .withAutomaticReconnect()
            .build();

        const startStream = () => {
            streamRef.current?.dispose();
            streamRef.current = connection
                .stream<StreamPayload>("WatchStream", deviceId, null)
                .subscribe({
                    next: (payload) => {
                        let bytes: Uint8Array;
                        if (payload instanceof Uint8Array) {
                            bytes = payload;
                        } else if (typeof payload === "string") {
                            // SignalR JSON protocol sends byte[] as base64
                            const binary = atob(payload);
                            bytes = new Uint8Array(binary.length);
                            for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
                        } else {
                            bytes = Uint8Array.from(payload);
                        }
                        appendBytesAndParse(bytes);
                    },
                    error: (err) => {
                        pushMessage(`[${new Date().toLocaleTimeString()}] stream error: ${String(err)}`);
                        setStreamError(STREAM_WAIT_FAILED_MESSAGE);
                    },
                    complete: () => pushMessage(`[${new Date().toLocaleTimeString()}] stream complete`),
                });
        };

        connection.onreconnecting(() => setConnectionState(HubConnectionState.Reconnecting));
        connection.onreconnected(() => {
            setConnectionState(HubConnectionState.Connected);
            startStream();
        });
        connection.onclose(() => setConnectionState(HubConnectionState.Disconnected));

        connection
            .start()
            .then(() => {
                setConnectionState(HubConnectionState.Connected);
                startStream();
            })
            .catch((err) => {
                pushMessage(`[${new Date().toLocaleTimeString()}] connect error: ${String(err)}`);
                setConnectionState(HubConnectionState.Disconnected);
            });

        return () => {
            pushMessage('Return: Cleaning up connection...');
            streamRef.current?.dispose();
            streamRef.current = null;
            connection.stop();
        };
    }, [deviceId, hubUrl, authToken, useSignalR]);

    // Tick the wait timer once a second while we're connected to a device
    // stream but have no frames yet. If nothing arrives by a hard cap just past
    // the server's device-start timeout, surface a failure — this also covers a
    // device that registered a stream but never sent frames (never server-timed).
    useEffect(() => {
        const hasFrames = frames.length > 0;
        if (!useSignalR || hasFrames) {
            setWaitElapsed(0);
            setStreamError(null);
            return;
        }
        setStreamError(null);
        setWaitElapsed(0);
        const startedAt = Date.now();
        const hardCapSeconds = STREAM_WAIT_TIMEOUT_SECONDS + 5;
        const intervalId = window.setInterval(() => {
            const seconds = Math.floor((Date.now() - startedAt) / 1000);
            setWaitElapsed(seconds);
            if (seconds >= hardCapSeconds) {
                setStreamError(STREAM_WAIT_FAILED_MESSAGE);
                window.clearInterval(intervalId);
            }
        }, 1000);
        return () => window.clearInterval(intervalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- restart the wait only when the stream (re)starts, not on every frame
    }, [useSignalR, frames.length > 0, connectionState]);

    useEffect(() => {
        if (!isSimulating) return;
        startSimulation();
        return () => stopSimulation();
    }, [isSimulating]);

    // Mirror frames.length into a ref so the playback interval callback can
    // see the current value without re-subscribing on every frame arrival.
    // Putting frames.length in the effect deps below would reset setInterval
    // each time a frame streams in — and because setInterval doesn't fire
    // until after the period elapses, the timer would be torn down before it
    // ever ticked, making playback far slower than the selected fps.
    const framesLengthRef = useRef(frames.length);
    useEffect(() => {
        framesLengthRef.current = frames.length;
    }, [frames.length]);

    useEffect(() => {
        if (!isPlaying) return;
        if (playbackTimerRef.current !== null) {
            window.clearInterval(playbackTimerRef.current);
        }
        playbackTimerRef.current = window.setInterval(() => {
            setCurrentFrameIndex((prev) => {
                const len = framesLengthRef.current;
                if (len === 0) return 0;
                const maxIndex = len - 1;
                if (prev < maxIndex) return prev + 1;
                return prev;
            });
        }, 1000 / playbackFps);

        return () => {
            if (playbackTimerRef.current !== null) {
                window.clearInterval(playbackTimerRef.current);
                playbackTimerRef.current = null;
            }
        };
    }, [isPlaying, playbackFps]);

    const currentFrame = frames[currentFrameIndex];

    const fusionHistoryRef = useRef<{ timestamp: number; radarIds: number[]; fusedRadarIds: number[] }[]>([]);
    const [fusionRate, setFusionRate] = useState<number | null>(null);
    const [fusionSampleFrames, setFusionSampleFrames] = useState<number>(0);

    useEffect(() => {
        if (!currentFrame) return;

        const { radarIds, fusedRadarIds } = computeFrameFusionObservations(currentFrame);
        const now = Date.now();
        const cutoff = now - 20000;
        const history = fusionHistoryRef.current;
        history.push({ timestamp: now, radarIds, fusedRadarIds });
        while (history.length > 0 && history[0].timestamp < cutoff) {
            history.shift();
        }

        // Walk the window in order, grouping each trackId's observations into
        // episodes. An episode is a contiguous run of frames where the trackId
        // is present; a gap closes it. If any frame inside an episode has the
        // track fused, the whole episode counts as fused (the pre-association
        // frames don't penalize the ratio). This also protects against trackId
        // reuse across unrelated tracks, as long as there's a gap between them.
        let totalObservations = 0;
        let fusedObservations = 0;
        const openCount = new Map<number, number>();
        const openFused = new Map<number, boolean>();

        const closeEpisode = (trackId: number) => {
            const count = openCount.get(trackId) ?? 0;
            totalObservations += count;
            if (openFused.get(trackId)) fusedObservations += count;
            openCount.delete(trackId);
            openFused.delete(trackId);
        };

        for (const entry of history) {
            const idsThisFrame = new Set(entry.radarIds);
            for (const openId of Array.from(openCount.keys())) {
                if (!idsThisFrame.has(openId)) closeEpisode(openId);
            }
            const fusedThisFrame = new Set(entry.fusedRadarIds);
            for (const id of entry.radarIds) {
                openCount.set(id, (openCount.get(id) ?? 0) + 1);
                if (fusedThisFrame.has(id)) openFused.set(id, true);
            }
        }
        for (const openId of Array.from(openCount.keys())) closeEpisode(openId);

        setFusionRate(totalObservations > 0 ? (fusedObservations / totalObservations) * 100 : null);
        setFusionSampleFrames(history.length);
    }, [currentFrame]);

    const radarTargetDatasets = useMemo(
        () => buildRadarTargetDatasets(currentFrame, currentFrameIndex),
        [currentFrame, currentFrameIndex]
    );

    const fusionTargetDataset = useMemo(
        () => buildFusionTargetDatasets(currentFrame),
        [currentFrame]
    );

    const radarPointDatasets = useMemo(
        () => buildRadarPointDatasets(currentFrame),
        [currentFrame]
    );

    // Calibration values are valid for projection whenever the user has run
    // calibration this session OR the device arrived with calibrated offsets
    // already stored. hasRunCalibration stays session-only so the Apply button
    // can't re-apply already-applied values.
    const hasCalibration =
        hasRunCalibration ||
        props.fusionOffsetPitchRadians !== undefined ||
        props.fusionOffsetRollRadians !== undefined ||
        props.fusionOffsetYawRadians !== undefined;

    const calibrationAngles = { accPitch, accRoll, calPitch, calRoll, calYaw, deviceHeight, hasCalibration };

    const calibrationRadarDatasets = useMemo(
        () => buildCalibrationRadarDatasets(currentFrame, currentFrameIndex, fitter, calibrationAngles),
        [currentFrame, currentFrameIndex, fitter, calPitch, calRoll, calYaw, accPitch, accRoll, deviceHeight, hasCalibration]
    );

    const calibrationCameraDatasets = useMemo(
        () => buildCalibrationCameraDatasets(currentFrame, currentFrameIndex, IMAGE_HEIGHT, fitter, calibrationAngles),
        [currentFrame, currentFrameIndex, fitter, calPitch, calRoll, calYaw, accPitch, accRoll, deviceHeight, hasCalibration]
    );

    const cameraDatasets = useMemo(
        () => buildCameraDatasets(currentFrame, IMAGE_HEIGHT, INTR_SCALED, DIST, accPitch, accRoll, deviceHeight),
        [currentFrame, accPitch, accRoll, deviceHeight]
    );

    const inferredObjectBounds = useMemo(
        () => buildInferredObjectBounds(currentFrame, IMAGE_HEIGHT),
        [currentFrame]
    );

    const handleInferredObjectHover = (position: { x: number; y: number } | null) => {
        setHoveredInferredObject(findHoveredInferredObject(position, inferredObjectBounds));
    };

    const cameraTooltipCallback = (point: { [k: string]: any; x: number; y: number; z: number }, label: string) => {
        if (label?.startsWith("Radar Line")) {
            return `Radar angle line @ x=${point.x?.toFixed(1)}`;
        }
        if (label?.startsWith("Inferred Object")) {
            const confidence = point.confidence !== undefined ? ` conf=${point.confidence?.toFixed(2)}` : "";
            return `Class ${point.classId} type ${point.targetType}${confidence} @ (${point.x?.toFixed(1)}, ${point.y?.toFixed(1)})`;
        }
        return `(${point.x?.toFixed(1)}, ${point.y?.toFixed(1)})`;
    };

    const hoveredBadge = hoveredInferredObject && (
        <div className="absolute right-2 top-2 rounded bg-slate-900/90 px-3 py-2 text-xs text-slate-100">
            Class {hoveredInferredObject.classId} type {hoveredInferredObject.targetType} conf{" "}
            {hoveredInferredObject.confidence?.toFixed(2)} (#{hoveredInferredObject.inferredIndex})
        </div>
    );

    const renderCurrentDeviceSettings = () => (
        <div className="relative w-full max-w-[640px]" style={{ display: "inline-block", aspectRatio: "4 / 3" }}>
            <h2>Current Device Settings</h2>
            <p>Radar targets and camera targets from the device, rendered with the device's active settings. Targets that were associated by the current settings share a color; gray targets are unassociated.</p>
            <div className="my-2 inline-block rounded border border-slate-600 bg-slate-800/40 px-3 py-1 text-sm">
                <span className="text-slate-400">% radar targets fused (last 20s): </span>
                <span className="font-semibold">
                    {fusionRate !== null ? `${fusionRate.toFixed(1)}%` : "—"}
                </span>
                <span className="ml-2 text-xs text-slate-500">
                    ({fusionSampleFrames} frame{fusionSampleFrames === 1 ? "" : "s"})
                </span>
            </div>
            <DataChart
                bounds={{ minX: -30, maxX: 30, minY: -20, maxY: 100 }}
                targetDatasets={[...fusionTargetDataset]}
                xAxisLabel="X-Axis"
                yAxisLabel="Y-Axis"
                showLegend={false}
                customTooltipCallback={(point, label) => {
                    const kind = label?.startsWith("Camera") ? "Camera" : "Radar";
                    return `${kind} ID ${point.targetId} @ (${point.x?.toFixed(3)}, ${point.y?.toFixed(3)}, ${point.z?.toFixed(3)})`;
                }}
                height="420px"
            />
        </div>
    );

    const renderCalibrationSection = () => (
        <div>
            <h2>Calibration View</h2>
            <p><i>Before Calibration:</i> targets are rendered on the left as red dots based on normal scene parameters, and the VA targets are rendered as boxes. This is what will be used to automatically determine target matching for calibration.</p>
            <p><i>After Calibration:</i> targets on the left are now rendered with the calibration applied, and both sets of targets are rendered on the right using the calibration matrix. On the right side, red triangles represent the radar, and yellow circles represent the VA targets. Targets on the right should be very close to each other after calibration.</p>
            <div className="w-full max-w-[640px]" style={{ display: "inline-block", aspectRatio: "4 / 3" }}>
                <DataChart
                    datasets={calibrationCameraDatasets}
                    bounds={{ minX: 0, maxX: IMAGE_WIDTH, minY: 0, maxY: IMAGE_HEIGHT }}
                    xAxisLabel="Image X"
                    yAxisLabel="Image Y"
                    showLegend={false}
                    backgroundImageUrl={backgroundImageUrl}
                    onHover={handleInferredObjectHover}
                    customTooltipCallback={cameraTooltipCallback}
                    height="100%"
                />
                {hoveredBadge}
            </div>
            <div className="w-full max-w-[640px]" style={{ display: "inline-block", aspectRatio: "4 / 3" }}>
                <DataChart
                    bounds={{ minX: -30, maxX: 30, minY: -20, maxY: 100 }}
                    datasets={calibrationRadarDatasets}
                    xAxisLabel="X-Axis"
                    yAxisLabel="Y-Axis"
                    showLegend={false}
                    customTooltipCallback={(point, label) => {
                        const kind = label?.startsWith("Camera") ? "Camera" : "Radar";
                        return `${kind} ID ${point.targetId} @ (${point.x?.toFixed(3)}, ${point.y?.toFixed(3)}, ${point.z?.toFixed(3)})`;
                    }}
                    height="480px"
                />
            </div>

            <div className="w-full flex flex-col gap-3 justify-content-center place-content-center place-items-center" >
                <div className="flex flex-row justify-content-center place-content-center place-items-center gap-4">
                    <NumericInput label="Pitch (accelerometer)" allowFloat={true} className="inline" value={accPitch} readOnly={true} />
                    <NumericInput label="Calibrated Pitch" allowFloat={true} className="inline" value={calPitch} onChange={(val) => { if (val !== undefined) { setCalPitch(val); } }} />
                </div>
                <div className="flex flex-row justify-content-center place-content-center place-items-center gap-4">
                    <NumericInput label="Roll (accelerometer)" allowFloat={true} className="inline" value={accRoll} readOnly={true} />
                    <NumericInput label="Calibrated Roll" allowFloat={true} className="inline" value={calRoll} onChange={(val) => { if (val !== undefined) { setCalRoll(val); } }} />
                </div>
                <div className="flex flex-row  justify-content-center place-content-center place-items-center">
                    <NumericInput label="Device Height" allowFloat={true} className="inline" value={deviceHeight} onChange={(val) => { if (val !== undefined) { fitter?.setDeviceHeight(val); setDeviceHeight(val); } }} />
                    <NumericInput label="Calibrated Yaw" allowFloat={true} className="inline" value={calYaw} onChange={(val) => { if (val !== undefined) { setCalYaw(val); } }} />
                </div>
                {liveError && (
                    <div className="text-sm text-slate-300">
                        Reprojection RMSE: <span className="font-semibold">{isFinite(liveError.rmsePx) ? `${liveError.rmsePx.toFixed(2)} px` : "—"}</span>
                        <span className="ml-3 text-slate-400">mean {isFinite(liveError.meanPx) ? `${liveError.meanPx.toFixed(2)} px` : "—"}</span>
                        <span className="ml-3 text-slate-500 text-xs">
                            ({liveError.usedSamples} inliers, {liveError.droppedAsOutliers} dropped)
                        </span>
                    </div>
                )}
                <div className="flex flex-row gap-2">
                    <button onClick={() => doCalibration()}>Do Calibration</button>
                    {props.onApplyToDevice && (
                        <button
                            className="disabled:opacity-50"
                            onClick={() => applyToDevice()}
                            disabled={!hasRunCalibration || isApplyingToDevice}
                            title={!hasRunCalibration ? "Run calibration first" : undefined}
                        >
                            {isApplyingToDevice ? "Applying..." : "Apply Settings To Device"}
                        </button>
                    )}
                </div>
                {calibrationStatus && (
                    <div className="max-w-[560px] text-center text-sm text-red-500">{calibrationStatus}</div>
                )}
            </div>
        </div>
    );

    return (
        <div className="p-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="text-lg font-semibold">Data Viewer and Calibration Tools</div>
                </div>
                {props.isSuperAdmin && (
                    <button
                        className={`rounded px-3 py-1 text-sm ${showDebugTools ? "bg-slate-800" : "border border-slate-500"}`}
                        onClick={() => setShowDebugTools((v) => !v)}
                    >
                        {showDebugTools ? "Hide debugging tools" : "Show debugging tools"}
                    </button>
                )}
            </div>

            <div className="space-y-2">
                {showDebugTools && (
                    <div className="flex gap-2 text-sm">
                        <button
                            className={`rounded px-3 py-1 text-sm ${activeChartTab === "calibration" ? "bg-slate-800 ring-2" : "border border-slate-500"}`}
                            style={activeChartTab === "calibration" ? { "--tw-ring-color": "var(--brand-primary)" } as React.CSSProperties : undefined}
                            onClick={() => setActiveChartTab("calibration")}
                        >
                            Calibration View
                        </button>
                        <button
                            className={`rounded px-3 py-1 text-sm ${activeChartTab === "radar" ? "bg-slate-800 ring-2" : "border border-slate-500"}`}
                            style={activeChartTab === "radar" ? { "--tw-ring-color": "var(--brand-primary)" } as React.CSSProperties : undefined}
                            onClick={() => setActiveChartTab("radar")}
                        >
                            Radar View
                        </button>
                        <button
                            className={`rounded px-3 py-1 text-sm ${activeChartTab === "camera" ? "bg-slate-800 ring-2" : "border border-slate-500"}`}
                            style={activeChartTab === "camera" ? { "--tw-ring-color": "var(--brand-primary)" } as React.CSSProperties : undefined}
                            onClick={() => setActiveChartTab("camera")}
                        >
                            Camera View
                        </button>
                    </div>
                )}
                {currentFrame ? (
                    showDebugTools && activeChartTab === "radar" ? (
                        <div className="relative w-full max-w-[640px]" style={{ display: "inline-block", aspectRatio: "4 / 3" }}>
                            <h2>Radar View</h2>
                            <p>This view shows the point clouds and targets as they are associated in the raw data from the device.</p>
                            <DataChart
                                bounds={{ minX: -30, maxX: 30, minY: -20, maxY: 100 }}
                                targetDatasets={radarTargetDatasets}
                                datasets={radarPointDatasets}
                                xAxisLabel="X-Axis"
                                yAxisLabel="Y-Axis"
                                showLegend={false}
                                backgroundImageUrl=""
                                customTooltipCallback={(point) => {
                                    return `ID ${point.targetId} @ (${point.x?.toFixed(3)}, ${point.y?.toFixed(3)}, ${point.z?.toFixed(3)})`;
                                }}
                                height="420px"
                            />
                        </div>
                    ) : showDebugTools && activeChartTab === "camera" ? (
                        <div className="relative w-full max-w-[640px]" style={{ display: "inline-block", aspectRatio: "4 / 3" }}>
                            <h2>Camera View</h2>
                            <p>View the inference boxes from the VA overlayed with estimated radar signals. Here the red boxes represent an estimated area from the radar returns based on normal scene conditions.</p>
                            <DataChart
                                datasets={cameraDatasets}
                                bounds={{ minX: 0, maxX: IMAGE_WIDTH, minY: 0, maxY: IMAGE_HEIGHT }}
                                xAxisLabel="Image X"
                                yAxisLabel="Image Y"
                                showLegend={false}
                                backgroundImageUrl={backgroundImageUrl}
                                onHover={handleInferredObjectHover}
                                customTooltipCallback={cameraTooltipCallback}
                                height="100%"
                            />
                            {hoveredBadge}
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {renderCurrentDeviceSettings()}
                            {renderCalibrationSection()}
                        </div>
                    )
                ) : useSignalR ? (
                    renderStreamWaitStatus()
                ) : (
                    <div className="text-xs text-slate-500">Waiting for parsed frames...</div>
                )}
            </div>

            <div className="sticky bottom-0 z-10 -mx-3 mt-4 bg-background-top-nav border-t border-separators-page px-3 pb-2 pt-2">
                <div className="rounded border border-slate-800  p-3">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                        <button
                            className="rounded bg-slate-800 px-3 py-1 text-sm disabled:opacity-50"
                            onClick={() => setIsPlaying(true)}
                            disabled={!frames.length || isPlaying}
                        >
                            Play
                        </button>
                        <button
                            className="rounded border border-slate-400 px-3 py-1 text-sm disabled:opacity-50"
                            onClick={stopPlayback}
                            disabled={!isPlaying}
                        >
                            Pause
                        </button>
                        <button
                            className="rounded border border-slate-400 px-3 py-1 text-sm disabled:opacity-50"
                            onClick={() => {
                                stopPlayback();
                                resetStreamState();
                            }}
                            disabled={!frames.length}
                        >
                            Clear
                        </button>
                        <span className="text-xs text-slate-500">
                            {frames.length ? `Frame ${currentFrameIndex + 1}/${frames.length}` : "No frames parsed"}
                        </span>
                    </div>
                    <input
                        type="range"
                        min={0}
                        max={scrubMaxFrameIndex ?? Math.max(frames.length - 1, 0)}
                        value={Math.min(currentFrameIndex, scrubMaxFrameIndex ?? Math.max(frames.length - 1, 0))}
                        onMouseDown={() => {
                            stopPlayback();
                            setScrubMaxFrameIndex(Math.max(frames.length - 1, 0));
                        }}
                        onMouseUp={() => setScrubMaxFrameIndex(null)}
                        onTouchStart={() => {
                            stopPlayback();
                            setScrubMaxFrameIndex(Math.max(frames.length - 1, 0));
                        }}
                        onTouchEnd={() => setScrubMaxFrameIndex(null)}
                        onChange={(e) => {
                            stopPlayback();
                            setCurrentFrameIndex(Number(e.target.value));
                        }}
                        className="w-full"
                        disabled={!frames.length}
                    />
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span>Playback rate:</span>
                        <input
                            type="range"
                            min={1}
                            max={60}
                            value={playbackFps}
                            onChange={(e) => setPlaybackFps(Number(e.target.value))}
                            className="w-24"
                        />
                        <span>{playbackFps} fps</span>
                        <span className="italic text-slate-400">* Radar operates at 10 fps</span>
                    </div>
                </div>

                {showDebugTools && (
                <div>
                    <div className="mx-auto w-full  px-4 py-3">
                        <div className="flex w-full items-center justify-between text-sm">
                            <div className="flex items-center gap-2">
                                <button
                                    className={`rounded px-3 py-1 text-sm ${activeBottomPanel === "datasource" ? "bg-slate-800" : "border border-slate-600"}`}
                                    onClick={() => setActiveBottomPanel(activeBottomPanel === "datasource" ? null : "datasource")}
                                >
                                    DataSource
                                </button>
                                <button
                                    className={`rounded px-3 py-1 text-sm ${activeBottomPanel === "console" ? "bg-slate-800" : "border border-slate-600"}`}
                                    onClick={() => setActiveBottomPanel(activeBottomPanel === "console" ? null : "console")}
                                >
                                    Console Output
                                </button>
                            </div>

                        </div>
                        {activeBottomPanel === "datasource" && (
                            <div className="mt-3 space-y-3 rounded border border-slate-800 p-3">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div>
                                        <div className="text-xs text-slate-400">By default, data is streamed from the device. To use simulated data, select Simulated and upload a file.</div>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2 text-sm">
                                    <button
                                        className={`rounded px-3 py-1 text-sm ${dataSource === "device" ? "bg-slate-800" : "border border-slate-500"}`}
                                        onClick={() => setDataSource("device")}
                                    >
                                        Realtime Data
                                    </button>
                                    <button
                                        className={`rounded px-3 py-1 text-sm ${dataSource === "sim" ? "bg-slate-800" : "border border-slate-500"}`}
                                        onClick={() => setDataSource("sim")}
                                    >
                                        Simulated Data
                                    </button>
                                </div>
                                {dataSource === "sim" ? (
                                    <div className="space-y-2">
                                        <div className="text-sm font-semibold">Simulated stream</div>
                                        <div className="flex flex-wrap items-center gap-2 text-sm">
                                            <input
                                                type="file"
                                                onChange={async (e) => {
                                                    const file = e.target.files?.[0];
                                                    if (!file) return;
                                                    resetStreamState();
                                                    const buf = await file.arrayBuffer();
                                                    const bytes = new Uint8Array(buf);
                                                    setSimData(bytes);
                                                    setSimFileName(file.name);
                                                    setSimFileSize(bytes.length);
                                                    setSimChunkIndex(0);
                                                    pushMessage(`[${new Date().toLocaleTimeString()}] SIM loaded ${file.name} (${bytes.length} bytes)`);
                                                }}
                                            />
                                            <span className="text-xs text-slate-500">Stream Simulated Data: 1 chunk/sec (5 min file)</span>
                                            <button
                                                className="rounded bg-slate-800 px-3 py-1 text-sm disabled:opacity-50"
                                                onClick={() => {
                                                    setSimChunkIndex(0);
                                                    setIsSimulating(true);
                                                }}
                                                disabled={!simData || isSimulating}
                                            >
                                                Start
                                            </button>
                                            <button
                                                className="rounded border border-slate-400 px-3 py-1 text-sm disabled:opacity-50"
                                                onClick={stopSimulation}
                                                disabled={!isSimulating}
                                            >
                                                Stop
                                            </button>
                                            {simFileName && (
                                                <span className="text-xs text-slate-500">
                                                    {simFileName} chunk {simChunkIndex}/{simTotalChunks} ({simFileSize} bytes)
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div></div>
                                )}
                            </div>
                        )}
                        {activeBottomPanel === "console" && (
                            <div className="mt-3 rounded border border-slate-800 bg-warmGray-900 text-slate-100 font-mono text-xs p-3 max-h-64 overflow-auto">
                                {messages.length ? messages.map((line, i) => <div key={i}>{line}</div>) : "Waiting for data..."}
                            </div>
                        )}
                    </div>
                </div>
                )}
                {showDebugTools && (
                    <div className="w-full justify-end flex text-xs text-slate-400">
                        Connection: &nbsp;<span className="font-semibold">{HubConnectionState[connectionState]}</span>
                    </div>
                )}
            </div>

        </div>
    );
};
